import { Component, computed, effect, input, model, output, signal, untracked } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { ChipModule } from "primeng/chip";
import { BadgeModule } from "primeng/badge";
import { BottomSheetComponent } from "../bottom-sheet/bottom-sheet.component";
import {
  collectAvailableValues,
  countSelected,
  FilterDefinition,
  FilterOption,
  FilterSelection,
  filterItems,
  removeSelectedValue
} from "./filter-panel.model";

/**
 * Shared filter sheet: a list of sections that slides to the chosen section's values.
 *
 * Selections are staged in a draft and only leave the sheet through `applied`, so dismissing it
 * discards them. The caller owns the applied state, which lets one screen filter issues and
 * another filter lots off the same control.
 */
@Component({
  selector: "app-filter-panel",
  standalone: true,
  imports: [CommonModule, BottomSheetComponent, ButtonModule, ChipModule, BadgeModule],
  templateUrl: "./filter-panel.component.html",
  styleUrls: ["./filter-panel.component.scss"]
})
export class FilterPanelComponent<T> {
  isOpen = model<boolean>(false);
  /** The items being filtered; the sections' options are drawn from these. */
  items = input<readonly T[]>([]);
  definitions = input<FilterDefinition<T>[]>([]);
  /** Applied selection, re-staged as the draft every time the sheet opens. */
  selected = input<FilterSelection>({});
  /** Singular noun for the apply button, e.g. "item" renders "Show 3 items". */
  itemNoun = input<string>("item");
  title = input<string>("Filter By");
  testId = input<string>("filter-panel");

  readonly applied = output<FilterSelection>();

  readonly currentView = signal<"list" | "sub">("list");
  readonly activeCategoryId = signal<string | null>(null);
  readonly draftSelected = signal<FilterSelection>({});

  readonly draftCount = computed(() => countSelected(this.definitions(), this.draftSelected()));

  readonly matchingCount = computed(() =>
    filterItems(this.items(), this.definitions(), this.draftSelected()).length);

  readonly applyLabel = computed(() =>
    `Show ${this.matchingCount()} ${this.itemNoun()}${this.matchingCount() === 1 ? "" : "s"}`);

  readonly activeCategory = computed(() =>
    this.definitions().find(definition => definition.id === this.activeCategoryId()));

  readonly activeCategoryValues = computed<FilterOption[]>(() => {
    const category = this.activeCategory();

    if (!category) {
      return [];
    }

    const draft = this.draftSelected();
    const options = category.availableValues.map(value => ({
      value,
      label: category.getLabel(value),
      selected: (draft[category.id] ?? []).includes(value)
    }));

    return options.sort(category.compareOptions
      ?? ((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true })));
  });

  readonly activeCategoryAllSelected = computed(() => {
    const category = this.activeCategory();

    if (!category || category.availableValues.length === 0) {
      return false;
    }

    const draft = this.draftSelected();

    return category.availableValues.every(value => (draft[category.id] ?? []).includes(value));
  });

  readonly draftChips = computed(() =>
    this.definitions().flatMap(definition =>
      (this.draftSelected()[definition.id] ?? []).map(value => ({
        filterId: definition.id,
        value,
        // A choice section always holds a value, so there is nothing to remove.
        removable: !definition.isChoice,
        displayLabel: this.chipLabel(definition, value)
      }))));

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        untracked(() => {
          collectAvailableValues(this.items(), this.definitions());
          this.draftSelected.set({ ...this.selected() });
          this.currentView.set("list");
          this.activeCategoryId.set(null);
        });
      }
    });
  }

  categoryCount(id: string): number {
    return (this.draftSelected()[id] ?? []).length;
  }

  /** Text for a choice section's row pill, standing in for the count badge. */
  choiceLabel(definition: FilterDefinition<T>): string {
    const value = (this.draftSelected()[definition.id] ?? [])[0];

    return value ? this.chipLabel(definition, value) : "";
  }

  private chipLabel(definition: FilterDefinition<T>, value: string): string {
    return definition.isChoice
      ? (definition.getChoiceLabel ?? definition.getLabel)(value)
      : definition.getLabel(value);
  }

  openCategory(id: string): void {
    this.activeCategoryId.set(id);
    this.currentView.set("sub");
  }

  backToList(): void {
    this.currentView.set("list");
    this.activeCategoryId.set(null);
  }

  toggleDraftValue(filterId: string, value: string): void {
    const isChoice = this.definitions().find(definition => definition.id === filterId)?.isChoice;

    this.draftSelected.update(current => {
      if (isChoice) {
        return { ...current, [filterId]: [value] };
      }

      const values = current[filterId] ?? [];

      return {
        ...current,
        [filterId]: values.includes(value) ? values.filter(v => v !== value) : [...values, value]
      };
    });
  }

  selectAllForCategory(filterId: string): void {
    const category = this.definitions().find(definition => definition.id === filterId);

    if (!category) {
      return;
    }

    this.draftSelected.update(current => ({ ...current, [filterId]: [...category.availableValues] }));
  }

  clearCategory(filterId: string): void {
    this.draftSelected.update(current => ({ ...current, [filterId]: [] }));
  }

  clearAllDraft(): void {
    // Clearing drops the filters but a choice section always holds a value, so its pick survives.
    this.draftSelected.update(current => this.definitions()
      .filter(definition => definition.isChoice)
      .reduce<FilterSelection>(
        (kept, definition) => ({ ...kept, [definition.id]: current[definition.id] ?? [] }),
        {}
      ));
  }

  removeDraftChip(filterId: string, value: string): void {
    this.draftSelected.update(current => removeSelectedValue(current, filterId, value));
  }

  apply(): void {
    this.applied.emit(this.draftSelected());
    this.isOpen.set(false);
  }
}
